import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardAppShell from '../components/DashboardAppShell';
import NovaSidebarWithCollapse from '../components/NovaSidebarWithCollapse';
import { CommunityFeed } from '../components/CommunityFeed';
import { useApp } from '../context/AppContext';
import { DEMO_PRODUCT_INPUT_SCOPE_CLASS } from '../designSystem';
import { CHAPTER_THEMES } from '../data/chapterThemes';
import {
  SARAH_THREAD,
  CLINICAL_NOTE_THREAD,
  TAKING_A_BREATH_THREAD,
} from '../data/communityDemoThreads';
import { FriendsTab, MyPostsTab } from './Community';
import {
  FertilityCommunityLiveTab,
  FertilityCommunitySpacesTab,
} from './FertilityCommunityConnectTabPanels';
import './Community.fertility.css';
import './WomenLikeMe.fertility.css';

const CHAPTER_META = { label: 'Fertility', emoji: '🌸' };

const FERTILITY_COMMUNITY_TABS = [
  { key: 'feed', label: 'My Chapter' },
  { key: 'live', label: 'Live rooms' },
  { key: 'groups', label: 'Spaces' },
  { key: 'friends', label: 'Friends' },
  { key: 'myposts', label: 'My posts' },
];

/**
 * Fertility demo only — /demo/fertility/community. Matches Connect design system (WomenLikeMe.fertility).
 */
export default function FertilityCommunityDemo() {
  const navigate = useNavigate();
  const { setNovaDraft } = useApp();
  const [activeTab, setActiveTab] = useState('feed');
  const [feedSort, setFeedSort] = useState('recent');

  const accent = CHAPTER_THEMES.fertility.accent;
  const demoChapterRaw = 'fertility';
  const chapterKey = 'fertility';
  const profileBasePath = `/demo/${demoChapterRaw}/profile`;
  const connectPath = `/demo/${demoChapterRaw}/connect`;

  const posts = useMemo(
    () => [
      { id: 'sarah', ...SARAH_THREAD.post, hasNovaThread: true, threadData: SARAH_THREAD },
      { id: 'clinical', ...CLINICAL_NOTE_THREAD.post, hasNovaThread: true, threadData: CLINICAL_NOTE_THREAD },
      { id: 'breath', ...TAKING_A_BREATH_THREAD.post, hasNovaThread: true, threadData: TAKING_A_BREATH_THREAD },
      {
        name: 'Maya',
        authorSlug: 'maya',
        initials: 'M',
        avatarBg: '#FBEAF0',
        avatarColor: '#993556',
        badge: 'friend',
        badgeStyle: { bg: '#E1F5EE', color: '#0F6E56', border: 'rgba(29,158,117,0.2)' },
        date: '3/22/2026',
        text: 'Anyone else balancing sleep recovery and pumping? Looking for what helped you feel human again.',
        hearts: 12,
        sameBoat: 3,
        replies: 2,
      },
      {
        name: 'Rina',
        authorSlug: 'rina',
        initials: 'R',
        avatarBg: '#E6F1FB',
        avatarColor: '#185FA5',
        badge: 'community',
        badgeStyle: { bg: 'var(--color-background-secondary)', color: 'var(--color-text-tertiary)', border: 'var(--color-border-secondary)' },
        date: '3/22/2026',
        text: 'Trigger shot tonight. Sharing my checklist in case it helps anyone else feeling anxious.',
        hearts: 8,
        sameBoat: 5,
        replies: 1,
      },
      {
        novaInsert: true,
        chapter: chapterKey,
        insertType: 'resource',
        tag: 'MENTAL HEALTH',
        headline: 'Coping with the two-week wait',
        body: "The TWW is one of the hardest stretches in this process. Here's what's helped others — and what to expect while you wait.",
        cta: { label: 'Read more →' },
        date: '3/22/2026',
        hearts: 24,
        replies: 6,
      },
      {
        name: 'Priya',
        authorSlug: 'priya',
        initials: 'P',
        avatarBg: '#EEEDFE',
        avatarColor: '#534AB7',
        badge: 'community',
        badgeStyle: { bg: 'var(--color-background-secondary)', color: 'var(--color-text-tertiary)', border: 'var(--color-border-secondary)' },
        date: '3/22/2026',
        text: 'Just got my fertilization report — 8 out of 11 fertilized. Cautiously optimistic 💕',
        hearts: 31,
        sameBoat: 2,
        replies: 4,
      },
    ],
    [chapterKey],
  );

  const seeAll = () => navigate('/signup');

  return (
    <DashboardAppShell
      fontFamily="'Plus Jakarta Sans', -apple-system, sans-serif"
      shellBackground="var(--sand)"
      mainStyle={{ background: 'rgb(249, 246, 241)', minHeight: '100%' }}
      mainClassName={DEMO_PRODUCT_INPUT_SCOPE_CLASS}
      rightColumn={
        <NovaSidebarWithCollapse
          navigate={navigate}
          setNovaDraft={setNovaDraft}
          chapter="fertility"
          section="community"
          className={DEMO_PRODUCT_INPUT_SCOPE_CLASS}
        />
      }
    >
      <div className="com-fertility-ds">
        <div className="com-fertility-layout">
          <div className="com-fertility-layout__main">
            <header className="com-page-title-card">
              <div className="com-page-title-card__main">
                <div className="com-page-eyebrow">🌸 Community · Fertility</div>
                <h1 className="com-page-title">Your fertility community</h1>
                <p className="com-page-sub">Nova is always in the room — watching, matching, and ready to help.</p>
                <div className="com-page-chips">
                  <span className="com-status-chip">12,450 members</span>
                  <span className="com-status-chip com-status-chip--live">
                    <span className="com-status-chip__dot" aria-hidden />
                    142 online now
                  </span>
                  <span className="com-chapter-chip">🌸 Fertility chapter — everyone here can see your posts.</span>
                </div>
              </div>
              <div className="com-page-title-card__right">
                <button type="button" className="com-new-post-btn" onClick={() => navigate('/signup')}>
                  + New post
                </button>
              </div>
            </header>

            <section className="com-section">
              <div className="com-discover-header">
                <div>
                  <div className="com-discover-eyebrow">My Chapter</div>
                  <h2 className="com-discover-title">What&apos;s happening right now</h2>
                  <p className="com-discover-sub">
                    Posts from women in your cycle, chapter, and stage — sorted by Nova.
                  </p>
                </div>
                <button type="button" className="com-discover-see-all" onClick={seeAll}>
                  See all →
                </button>
              </div>

              <div className="com-tab-bar" role="tablist">
                {FERTILITY_COMMUNITY_TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === t.key}
                    className={`com-tab${activeTab === t.key ? ' com-tab--active' : ''}`}
                    onClick={() => setActiveTab(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {activeTab === 'feed' && (
                <>
                  <div className="com-composer">
                    <div className="com-composer__row">
                      <div className="com-composer-av">M</div>
                      <input
                        type="text"
                        className="com-composer-input"
                        readOnly
                        placeholder="Share something with your chapter..."
                        aria-label="Compose post"
                      />
                    </div>
                    <div className="com-composer-actions">
                      <button type="button" className="com-composer-media-btn">
                        📷 Photo
                      </button>
                      <button type="button" className="com-composer-media-btn">
                        🎥 Video
                      </button>
                      <button type="button" className="com-composer-media-btn">
                        GIF
                      </button>
                      <span className="com-composer-chapter-pill">
                        🌸 {CHAPTER_META.label}
                      </span>
                      <button type="button" className="com-composer-post-btn" onClick={() => navigate('/signup')}>
                        Post
                      </button>
                    </div>
                  </div>

                  <div className="com-feed-sort">
                    <span className="com-feed-sort__label">What&apos;s happening in your chapter</span>
                    <div className="com-feed-sort__btns">
                      <button
                        type="button"
                        className={`com-feed-sort__btn${feedSort === 'recent' ? ' com-feed-sort__btn--active' : ''}`}
                        onClick={() => setFeedSort('recent')}
                      >
                        Recent
                      </button>
                      <button
                        type="button"
                        className={`com-feed-sort__btn${feedSort === 'top' ? ' com-feed-sort__btn--active' : ''}`}
                        onClick={() => setFeedSort('top')}
                      >
                        Top
                      </button>
                    </div>
                  </div>

                  <CommunityFeed
                    chapter={chapterKey}
                    items={posts}
                    onOpenNova={() => navigate('/nova')}
                    profileBasePath={profileBasePath}
                    feedSkin="fertilityCommunity"
                  />
                </>
              )}

              {activeTab === 'live' && <FertilityCommunityLiveTab navigate={navigate} />}
              {activeTab === 'groups' && <FertilityCommunitySpacesTab navigate={navigate} />}
              {activeTab === 'friends' && (
                <FriendsTab
                  navigate={navigate}
                  profileBasePath={profileBasePath}
                  connectPath={connectPath}
                  accent={accent}
                  chapterKey={chapterKey}
                />
              )}
              {activeTab === 'myposts' && (
                <MyPostsTab navigate={navigate} profileBasePath={profileBasePath} connectPath={connectPath} accent={accent} />
              )}
            </section>
          </div>
        </div>
      </div>
    </DashboardAppShell>
  );
}
